import { Component, EventEmitter, Input, OnInit, Optional, Output, inject, input, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { merge, Observable, scan, startWith, Subject } from 'rxjs';
import { AppService } from '../../app.service';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { generalConfigFromGlobal } from '../../shared/workspace-element-context';
import { ScreenVideo } from '../../screen-video-container/screen-video-list/screen-video';
import { ScreenVideoDeleteComponent } from '../../screen-video-container/screen-video-list/screen-video-delete.component';
import { ScreenVideoDialogComponent } from '../../screen-video-container/screen-video-list/screen-video-dialog/screen-video-dialog.component';
import { ScreenVideoListService } from '../../screen-video-container/screen-video-list/screen-video-list.service';
import { UserService } from '../../screen-video-container/user.service';
import { ScreenDocumentsComponent } from '../screen-documents.component';

@Component({
    selector: 'gtw-screen-videos',
    templateUrl: './screen-videos.component.html',
    styleUrls: ['./screen-videos.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ScreenVideosComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);


  dialogRef: MatDialogRef<ScreenVideoDialogComponent, any> | undefined;
  videoList$!: Observable<any>;
  merged$!: Observable<any>;
  deleteSubject = new Subject();
  isLoading = false;
  durationInSeconds = 5;
  currentUser: string = '';

  @Output()
  updateScreensVideoEvent = new EventEmitter();

  @Input('from-home')
  fromHome: boolean = false;

  readonly screenKeyInput = input<number | undefined>(undefined, { alias: 'screen-key' });
  readonly clientKeyInput = input<number | undefined>(undefined, { alias: 'client-key' });
  readonly baseUrlInput = input<string | undefined>(undefined, { alias: 'base-url' });
  readonly userAccess = input(0, { alias: 'user-access' });

  @Output('close-model') closeModal = new EventEmitter<any>();

  @Input('refresh')
  set refresh(value: boolean) {
    if (value) {
      this.getAllVideos();
    }
  }

  @Output()
  cancelDocModal = new EventEmitter<string>();

  constructor(
    public gtwGlobal: GlobalService,
    public gtwWorkspace: WorkspaceService,
    public gtwApp: AppService,
    private screenVideoListService: ScreenVideoListService,
    public matDialog: MatDialog,
    private userService: UserService,
    private _snackBar: MatSnackBar,
    @Optional() public dialogDocRef: MatDialogRef<ScreenDocumentsComponent>
  ) {}

  ngOnInit(): void {
    this.currentUser = this.userService.getUser()?.sso || '';
    if (!isNaN(this.clientKeyNum) && !isNaN(this.screenKeyNum)) {
      this.getAllVideos();
    }
  }

  private activeScreen(): any {
    return this.gtwWorkspace.workspaceFactory?.activeScreen || {};
  }

  private getBaseUrl(): string {
    const baseUrlInput = this.baseUrlInput();
    if (baseUrlInput) {
      return baseUrlInput;
    }
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    return String(gc.base_url ?? gc.api ?? '');
  }

  get screenKeyNum(): number {
    const screenKeyInput = this.screenKeyInput();
    if (screenKeyInput != null && !isNaN(Number(screenKeyInput))) {
      return Number(screenKeyInput);
    }
    const a = this.activeScreen();
    const raw = a.link ?? a.screenKey ?? a.screen_key;
    return parseInt(String(raw), 10);
  }

  get clientKeyNum(): number {
    const clientKeyInput = this.clientKeyInput();
    if (clientKeyInput != null && !isNaN(Number(clientKeyInput))) {
      return Number(clientKeyInput);
    }
    const raw =
      this.gtwGlobal.global?.globalParams?.client_key ?? this.gtwApp.USER_SETTINGS?.user?.client_key;
    return parseInt(String(raw), 10);
  }

  canUploadVideos(): boolean {
    return !this.fromHome && this.userAccess() >= 3;
  }

  private getAllVideos() {
    this.isLoading = true;
    this.videoList$ = this.screenVideoListService.getAllVideos(
      this.getBaseUrl(),
      this.clientKeyNum,
      this.screenKeyNum
    );
    this.merged$ = merge(this.videoList$, this.deleteSubject).pipe(
      startWith({}),
      scan((videoList, data) => {
        if (data && Object.keys(data).length > 0) {
          this.isLoading = false;
        }
        if (data.operation && data.operation == 'delete') {
          const index = videoList.data.findIndex((elt: ScreenVideo) => elt.video_id === data.id);
          videoList.data.splice(index, 1);
          return videoList;
        }
        return data;
      })
    );
  }

  async openVideoDialog(video: ScreenVideo) {
    if (this.dialogDocRef) {
      this.dialogDocRef?.close();
    }
    this.cancelDocModal.emit('cancel-document-modal');

    this.matDialog.closeAll();
    this.dialogRef?.close();

    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = { ...video, ...{ baseURL: this.getBaseUrl() } };
    dialogConfig.panelClass = 'remove-dialog-css';
    dialogConfig.hasBackdrop = false;
    dialogConfig.width = '30vw';
    (dialogConfig.position = { right: '0px', top: '55vh' }),
      (dialogConfig.id = video.video_id.toString());

    this.dialogRef = undefined;
    this.dialogRef = this.matDialog.open(ScreenVideoDialogComponent, dialogConfig);
    const event = new Event('playvideo');
    document.dispatchEvent(event);
  }

  isVideoEditable = false;
  updateScreensVideoInput!: ScreenVideo;

  updateScreenVideo(video: ScreenVideo): void {
    this.isVideoEditable = true;
    this.updateScreensVideoInput = video;
    this.updateScreensVideoEvent.emit(video);
  }

  deleteScreenVideo(id: number): void {
    this.isLoading = true;
    this.screenVideoListService.deleteScreenVideo(this.getBaseUrl(), this.clientKeyNum, id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res: any) => {
        this.isLoading = false;
        if (res.callSuccess == 1 || parseInt(res.callSuccess) == 1) {
          this.deleteSubject.next({ operation: 'delete', id: id });
          this.openSnackBar('Video deleted Successfully !!');
        } else {
          this.openSnackBar('Video delete failed: ' + res.errorMessage);
        }
        this.cancelDocModal.emit('cancel-document-modal');
      },
      (error) => {
        this.openSnackBar('Video delete Failed');
        console.log(error);
      }
    );
  }

  isValidUser(sso: string) {
    if (this.userService.isValidUser(['admin']) && this.userService.getUser()?.sso == sso) {
      return true;
    }
    if (this.userService.isValidUser(['super admin', 'developer'])) {
      return true;
    }

    return false;
  }

  private openSnackBar(message: string) {
    this._snackBar.open(message, '', {
      duration: this.durationInSeconds * 1000,
    });
  }

  confirmDelete(id: number): void {
    this.deleteScreenVideo(id);
  }

  refreshScreenVideoList(_event: boolean) {
    this.isVideoEditable = false;

    if (!isNaN(this.clientKeyNum) && !isNaN(this.screenKeyNum)) {
      this.getAllVideos();
      setTimeout(() => {}, 300);
    }
  }

  cancel() {
    this.closeModal.emit('true');
    this.cancelDocModal.emit('document-modal-cancel');
  }
}
